import { Caret, fromTheme, Icon, Link, Pressable, styled, View, webOnlyStyles, Text } from 'lib_ui-primitives';

// Container for submenu and its items, JUST to order them properly
export const MenuPlusSubMenus = styled(View).attrs({ name: 'menu-plus-sub-menus' })`
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
`;

// the actual anchor link that wraps the menu item
// styling wise, only provides the active/focus outline
// padding is hardcoded as it is required for the outline to show
let _NavigationButton = styled(Link).attrs({ name: 'navigation-button', role: 'menuitem' })`
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    text-decoration-line: none;

    border-radius: ${fromTheme('outerMenus', 'borderRadius')};
    padding: 2px;
`;
_NavigationButton = webOnlyStyles(_NavigationButton)`
    &:focus-visible,
    &:active {
        outline: none; /* Removes the default border when active */
        background-color: ${fromTheme('outerMenus', 'focus', 'borderColor')};
    }
`;
export const NavigationButton = _NavigationButton;

// this is what the user will interpret as the button
export const MenuButton = styled(View).attrs({ name: 'menu-button' })`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    flex-grow: 1;
    flex-shrink: 0;
    padding: ${({ iconOnly, theme }) => (iconOnly ? theme.outerMenus.iconOnly?.padding : theme.outerMenus.padding)};
    transition: padding 0.3s ease-in-out;
    border-radius: ${fromTheme('outerMenus', 'borderRadius')};
    cursor: pointer;
    min-height: ${fromTheme('outerMenus', 'minHeight')};

    background-color: ${({ selected, theme }) =>
        selected ? theme.outerMenus.active?.backgroundColor : theme.outerMenus.backgroundColor};

    &:hover {
        ${({ selected, theme }) =>
            selected
                ? `
                background-color: ${theme.outerMenus.active?.backgroundColorHover};`
                : `
                background-color: ${theme.outerMenus.hover?.backgroundColor};
                color: ${theme.outerMenus.hover?.fontColor};
            `}
    }
`;
MenuButton.displayName = 'MenuButton';

export const disabledNavigationButton = styled(View).attrs({
    name: 'disabled-navigation-button',
    role: 'menuitem',
    'aria-disabled': true
})`
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
`;

export const DisabledMenuButton = styled(View).attrs({ name: 'disabled-button' })`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    flex-grow: 1;
    flex-shrink: 0;
    padding: ${({ iconOnly, theme }) => (iconOnly ? theme.outerMenus.iconOnly?.padding : theme.outerMenus.padding)};
    transition: padding 0.3s ease-in-out;
    border-radius: ${fromTheme('outerMenus', 'borderRadius')};
    cursor: not-allowed;
    min-height: ${fromTheme('outerMenus', 'minHeight')};

    background-color: transparent;
`;
DisabledMenuButton.displayName = 'DisabledMenuButton';

let _SubMenu = styled(Pressable).attrs({ name: 'sub-menu', tabIndex: 0, role: 'menuitem', 'aria-haspopup': 'true' })`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    flex-grow: 1;
    flex-shrink: 0;
    padding: ${({ iconOnly, theme }) => (iconOnly ? theme.outerMenus.iconOnly?.padding : theme.outerMenus.padding)};
    transition: padding 0.3s ease-in-out;
    border-radius: ${fromTheme('outerMenus', 'borderRadius')};
    cursor: pointer;
    min-height: ${fromTheme('outerMenus', 'minHeight')};

    background-color: ${({ selected, isCollapsed, iconOnly, isSubSubMenu, theme }) => {
        /**
         * background color:
         * |                                 | sub menu         | sub sub menu (isSubSubMenu)   |
         * |---------------------------------|------------------|-------------------------------|
         * | Active - (Expanded) - IconOnly  | Active           | n/a                           |
         * | active - Expanded - !IconOnly   | Hover            | SubSub                        |
         * | Active - Collapsed - IconOnly   | Active           | n/a                           |
         * | Active - Collapsed - !IconOnly  | Active           | Active                        |
         * | !active - (Expanded) - IconOnly | backgroundColor  | n/a                           |
         * | !active - Expanded - !IconOnly  | Hover            | SubSub                        |
         * | !active - Collapsed - IconOnly  | backgroundColor  | n/a                           |
         * | !active - Collapsed - !IconOnly | backgroundColor  | backgroundColor               |
         *
         */
        if (!isCollapsed && !iconOnly) {
            return isSubSubMenu
                ? theme.outerMenus.subSubMenu?.backgroundColor
                : theme.outerMenus.hover?.backgroundColor;
        }
        if (selected) {
            return theme.outerMenus.active?.backgroundColor;
        }

        return theme.outerMenus.backgroundColor;
    }};

    &:hover {
        ${({ selected, isCollapsed, theme }) =>
            isCollapsed && selected
                ? `
                background-color: ${theme.outerMenus.active?.backgroundColorHover};`
                : `
                background-color: ${theme.outerMenus.hover?.backgroundColor};
                color: ${theme.outerMenus.hover?.fontColor};

        `}
    }
`;

_SubMenu = webOnlyStyles(_SubMenu)`
    border: solid 2px transparent;

    &:focus-visible,
    &:active {
        outline: none; /* Removes the default border when active */
        border: solid 2px ${fromTheme('outerMenus', 'focus', 'borderColor')};
    }
`;
export const SubMenu = _SubMenu;

export const SubMenuChildrenArea = styled(View).attrs({ name: 'sub-menu-children-area' })`
    display: ${({ isCollapsed }) => (isCollapsed ? 'none' : 'flex')};
    flex-direction: column;
    gap: ${fromTheme('outerMenus', 'subMenu', 'gap')};
    padding-top: ${fromTheme('outerMenus', 'subMenu', 'paddingTop')};
    padding-bottom: ${fromTheme('outerMenus', 'subMenu', 'paddingBottom')};
    margin-bottom: ${fromTheme('outerMenus', 'subMenu', 'marginBottom')};
`;

export const NoIcon = styled(View).attrs({ name: 'no-icon' })`
    width: ${fromTheme('outerMenus', 'iconWidth')};
    flex-shrink: 0;
    flex-grow: 0;
`;

// do NOT put a "name" attribute here, as it will conflict with mechanism
// that passes the (icon) name prop to the Icon component
export const NavIcon = styled(Icon).attrs(({ theme, fontColor }) => {
    return {
        fontColor: fontColor || theme.outerMenus?.fontColor,
        size: 'md'
    };
})`
    width: ${fromTheme('outerMenus', 'iconWidth')}px;
    flex-shrink: 0;
    flex-grow: 0;

    & svg {
        flex-shrink: 0;
    }
`;

export const LinkText = styled(Text).attrs({ name: 'link-text' })`
    text-wrap: auto;
    flex-basis: ${fromTheme('outerMenus', 'textWidth')};
    flex-grow: 1;
    flex-shrink: 0;
    ${fromTheme('outerMenus', 'font')};
    color: ${({ theme, selected, disabled }) =>
        disabled
            ? theme.outerMenus.disabled?.fontColor
            : selected
              ? theme.outerMenus.active.fontColor
              : theme.outerMenus.fontColor};
    padding-left: ${({ hidden }) => (hidden ? 0 : 12)}px;
    padding-right: ${({ hidden }) => (hidden ? 0 : 8)}px;
    opacity: ${({ hidden }) => (hidden ? 0 : 1)};
    max-width: ${({ hidden }) => (hidden ? 0 : 'none')};
    transition:
        padding 0.3s ease-in-out,
        opacity 0.3s ease-in-out,
        width 0.3s ease-in-out;
`;

// resulted in this briefly displaying text instead of the icon).
export let SubMenuChevron = styled(Caret).attrs({ name: 'collapse-indicator' })`
    stroke: ${({ theme, selected }) => (selected ? theme.outerMenus.active?.fontColor : theme.outerMenus.fontColor)};
    opacity: ${({ hidden }) => (hidden ? 0 : 1)};
    max-width: ${({ hidden }) => (hidden ? 0 : 'none')};
    margin-right: ${fromTheme('outerMenus', 'subMenu', 'chevron', 'marginRight')};
    margin-left: ${fromTheme('outerMenus', 'subMenu', 'chevron', 'marginLeft')};
    width: ${fromTheme('outerMenus', 'subMenu', 'chevron', 'width')};
    height: ${fromTheme('outerMenus', 'subMenu', 'chevron', 'height')};
    transition:
        padding 0.3s ease-in-out,
        opacity 0.3s ease-in-out,
        width 0.3s ease-in-out;
`;
SubMenuChevron.displayName = 'SubMenuChevron';
SubMenuChevron = webOnlyStyles(SubMenuChevron)`
    cursor: pointer;
`;
